import { Player } from '../../../domain/entities';

/**
 * FormProcessor - Handles player form changes
 *
 * Responsibilities:
 * - Update form for players who played (potential improvement)
 * - Update form for players who didn't play (potential decline)
 * - Track consecutive bad performances
 */
export class FormProcessor {
    private static readonly MAX_FORM = 3;
    private static readonly MIN_FORM = -3;
    private static readonly GOOD_RATING_THRESHOLD = 6.5;
    private static readonly BAD_RATING_THRESHOLD = 6.0;

    /**
     * Update form for all players based on whether they played
     */
    static processFormChanges(
        allPlayers: Player[],
        playersWhoPlayed: Set<number>
    ): void {
        allPlayers.forEach(player => {
            // Only update form if not injured
            if (!player.injury) {
                if (playersWhoPlayed.has(player.id)) {
                    this.updateFormForActivePlayer(player);
                } else {
                    this.updateFormForInactivePlayer(player);
                }
            }
        });
    }

    /**
     * Track recent ratings and consecutive bad form games
     */
    static trackPlayerRatings(
        player: Player,
        rating: number
    ): void {
        // Initialize recent ratings if needed
        if (!player.recentRatings) {
            player.recentRatings = [];
        }

        // Add new rating
        player.recentRatings.push(rating);

        // Keep only last 5 ratings
        if (player.recentRatings.length > 5) {
            player.recentRatings.shift();
        }

        // Track consecutive bad form games
        if (typeof player.consecutiveBadFormGames === 'undefined') {
            player.consecutiveBadFormGames = 0;
        }

        if (rating < this.BAD_RATING_THRESHOLD) {
            player.consecutiveBadFormGames++;
        } else if (rating > this.GOOD_RATING_THRESHOLD) {
            player.consecutiveBadFormGames = 0;
        }
    }

    /**
     * Form tends to improve when playing (40% chance if below max)
     */
    private static updateFormForActivePlayer(player: Player): void {
        if (player.form < this.MAX_FORM && Math.random() < 0.4) {
            player.form = Math.max(
                this.MIN_FORM,
                Math.min(this.MAX_FORM, player.form + 1)
            );
        }
    }

    /**
     * Form tends to decline when not playing (20% chance if above min)
     */
    private static updateFormForInactivePlayer(player: Player): void {
        if (player.form > this.MIN_FORM && Math.random() < 0.2) {
            player.form = Math.max(
                this.MIN_FORM,
                Math.min(this.MAX_FORM, player.form - 1)
            );
        }
    }
}
